import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import CountrySelect from "@/components/CountrySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { lang, t } = useLang();
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [operatingCountry, setOperatingCountry] = useState("JO");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const msg = err?.message || t("login_google_error");
      toast.error(msg);
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error(lang === "ar" ? "أدخل البريد وكلمة المرور" : "Enter email and password");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "signup") {
        const result = await signUp(email.trim(), password, name.trim() || email.split("@")[0], operatingCountry);
        if (!result.session) {
          toast.success(
            lang === "ar"
              ? "تم إنشاء الحساب. تحقق من بريدك لتأكيد الحساب."
              : "Account created. Check your email to confirm.",
          );
          setMode("signin");
          return;
        }
      } else {
        await signIn(email.trim(), password);
      }
      navigate("/workspace", { replace: true });
    } catch (err) {
      const msg = err?.message || (lang === "ar" ? "فشل تسجيل الدخول" : "Sign in failed");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      <PublicSiteHeader showAuthActions={false} />

      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-4">
            {mode === "signup"
              ? lang === "ar" ? "إنشاء حساب" : "Create account"
              : lang === "ar" ? "تسجيل الدخول" : "Sign in"}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-slate-900 tracking-tight leading-[1.05] mb-4 text-balance">
            {t("login_title")}
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-8">{t("login_sub")}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-2">
                <Label htmlFor="name">{lang === "ar" ? "الاسم" : "Name"}</Label>
                <Input
                  id="name"
                  data-testid="signup-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-none"
                  autoComplete="name"
                />
              </div>
            )}
            {mode === "signup" && (
              <div className="space-y-2">
                <Label htmlFor="operating-country">{t("operating_country")}</Label>
                <CountrySelect
                  id="operating-country"
                  value={operatingCountry}
                  onChange={setOperatingCountry}
                  useApi={false}
                  data-testid="signup-country"
                />
                <p className="text-xs text-slate-500">{t("operating_country_hint")}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{lang === "ar" ? "البريد الإلكتروني" : "Email"}</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none"
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{lang === "ar" ? "كلمة المرور" : "Password"}</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                required
                minLength={6}
              />
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={submitting}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-12 px-6 text-sm gap-3 w-full"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {mode === "signup"
                ? lang === "ar" ? "إنشاء حساب" : "Create account"
                : lang === "ar" ? "تسجيل الدخول" : "Sign in"}
              <Arrow size={14} />
            </Button>
          </form>

          <button
            type="button"
            data-testid="auth-mode-toggle"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="mt-4 text-sm text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline"
          >
            {mode === "signin"
              ? lang === "ar" ? "ليس لديك حساب؟ أنشئ حساباً" : "No account? Sign up"
              : lang === "ar" ? "لديك حساب؟ سجّل الدخول" : "Have an account? Sign in"}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 text-slate-500">{t("login_or")}</span>
            </div>
          </div>

          <Button
            type="button"
            data-testid="google-signin-btn"
            variant="outline"
            disabled={submitting || googleLoading}
            onClick={handleGoogleSignIn}
            className="rounded-none h-12 w-full gap-3"
          >
            {googleLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            {t("login_google")}
          </Button>

          <Link
            to="/"
            data-testid="login-back-to-landing"
            className="mt-8 block text-center text-sm text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline"
          >
            {t("login_back")}
          </Link>
        </div>
      </div>
    </div>
  );
}
